/*
 * Copyright (c) Dito Technologies LLC
 */

import React from "react";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {ListenPlayer_Piece$data} from "ui/feature/piece-play/listen/__generated__/ListenPlayer_Piece.graphql";
import PieceRenderer from "ui/feature/piece-play/common/piece-renderer/PieceRenderer";
import Button from "ui/kit/buttons/Button";
import {faPlay} from "@fortawesome/free-solid-svg-icons/faPlay";
import PieceRendererStepThru from "ui/feature/piece-play/common/piece-renderer/PieceRendererStepThru";
import styles from "./ListenPlayer.module.scss";
import {NoteDisplayState} from "ui/feature/piece-play/common/PlayTypes";

interface Props {
    piece: ListenPlayer_Piece$data
}

const ListenPlayer: React.FC<Props> = ({piece}) => {

    const [audio, setAudio] = React.useState<HTMLAudioElement | null>(null)
    const [playPosition, setPlayPosition] = React.useState<number>(0)
    const [bulbIdToStateLookup, setBulbIdToStateLookup] = React.useState<{ [bulbId: string]: NoteDisplayState | undefined }>({});
    const [currentTime, setCurrentTime] = React.useState<number | null>(null)

    const executePlay = React.useCallback(() => {

        setCurrentTime(null);
        setPlayPosition(0);

        const downloadUrl = piece.file?.audio_file_download_url;
        if (!downloadUrl) {
            alert("Audio file not ready");
            // TODO: Nice alert here
            return
        }

        const nextAudio = new Audio(downloadUrl);
        nextAudio.play();
        nextAudio.onended = () => {
            nextAudio.pause()
            nextAudio.currentTime = 0
            setPlayPosition(0);
            setCurrentTime(null);
            setBulbIdToStateLookup({})
            setAudio(null);
            console.log("on ended");
        }
        setAudio(nextAudio);
    }, [piece, setPlayPosition, setBulbIdToStateLookup])

    React.useEffect(() => {
        const ci = setInterval(() => {
            if (audio) {
                setCurrentTime(audio.currentTime)
            }
        }, 30);
        return () => {
            clearInterval(ci);
        }
    }, [audio])

    const maxNoteTime = React.useMemo(() => {
        let maxTime = 0;
        for (const item of piece.file?.notes || []) {
            const currTime = item.note_start + item.note_duration;
            if (currTime > maxTime) {
                maxTime = currTime;
            }
        }
        return maxTime;
    }, [piece]);

    React.useEffect(() => {
        // update play position
        const duration = piece.file?.duration || 0;
        if (currentTime === null) {
            return;
        }
        if (maxNoteTime === 0) {
            return;
        }
        const currPercentageThroughAudio = currentTime / duration;
        const currPlayPosition = maxNoteTime * currPercentageThroughAudio;
        setPlayPosition(currPlayPosition);
    }, [setPlayPosition, maxNoteTime, currentTime, piece])

    React.useEffect(() => {
        // might be an opportunity to improve efficiency here
        const items = piece.file?.notes.reduce((prevValue, currValue) => {
            const startTime = currValue.note_start;
            const endTime = currValue.note_start + currValue.note_duration;
            if (playPosition > endTime) {
                (prevValue as any)[currValue.bulb_id] = "success" as NoteDisplayState;
            } else if (playPosition < startTime) {
                (prevValue as any)[currValue.bulb_id] = "none" as NoteDisplayState;
            } else {
                (prevValue as any)[currValue.bulb_id] = "current" as NoteDisplayState;
            }
            return prevValue;
        }, {}) || {};
        setBulbIdToStateLookup(items)
    }, [piece, playPosition, setBulbIdToStateLookup])

    return (
        <>
            <Button onClick={executePlay} icon={faPlay} title={"Play"}/>
            <PieceRenderer
                playType={"both_hands"}
                currentMidiNotes={[]}
                playPosition={playPosition}
                piece={piece}
                bulbIdToStateLookup={bulbIdToStateLookup}
            />
            <PieceRendererStepThru
                className={styles.stepThrough}
                piece={piece}
                currTime={playPosition}
                bulbIdToStateLookup={bulbIdToStateLookup}/>
        </>
    );
}

export default createFragmentContainer(ListenPlayer, {
    piece: graphql`
    fragment ListenPlayer_Piece on MusicPiece {
        id
        ...PieceRenderer_piece
        ...PieceRendererStepThru_piece
        file {
            audio_file_download_url
            duration
            notes {
                bulb_id
                note_start 
                note_duration
                midi_number
            }
        }
    }
    `
});
